import {
  IonBadge, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle,
  IonCardTitle, IonCheckbox, IonCol, IonContent, IonFooter, IonIcon,
  IonInput,
  IonItem,
  IonLabel, IonModal, IonSegment, IonSegmentButton, IonSpinner, IonText, IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import {
  businessOutline, chatbubbleEllipsesOutline, checkmarkCircleOutline,
  cloudDoneOutline, documentTextOutline, logoWhatsapp, phonePortraitOutline,
  refreshOutline, sendOutline, shieldCheckmarkOutline,
} from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isAllowedMetaMessageOrigin, loadMetaFacebookSdk } from '../../../../../Utils/MetaFacebookSdk';
import {
  requestService, whatsappMetaActivate, whatsappMetaDisable,
  whatsappMetaOnboardingComplete, whatsappMetaOnboardingSession,
  whatsappMetaReviewAssets, whatsappMetaReviewMessage, whatsappMetaReviewTemplate,
  whatsappMetaStatus,
  whatsappMetaTest,
} from '../../../../../Utils/Services';
import './WhatsAppMetaCard.css';

type Props = { empresaId: string };
type ReviewSection = 'messaging' | 'templates';
type ReviewTemplateSuggestion = {
  key: string;
  title: string;
  description: string;
  name: string;
  language: string;
  category: string;
  body: string;
  variables: Array<{ position: number; label: string; example: string }>;
};
type MetaStatus = {
  available: boolean;
  status: string;
  activeChannel: 'baileys' | 'meta_cloud_api' | 'none';
  reviewDemoAvailable?: boolean;
  integration?: {
    status?: string;
    wabaId?: string;
    phoneNumberId?: string;
    displayPhoneNumber?: string;
    verifiedName?: string;
    platformType?: string;
    isOnBizApp?: boolean;
    lastError?: { code?: string; message?: string };
  };
};
type ReviewAssets = {
  graphApiVersion: string;
  permissions: string[];
  waba: { id: string; name?: string; accountReviewStatus?: string };
  phone: {
    id: string;
    displayPhoneNumber?: string;
    verifiedName?: string;
    status?: string;
    qualityRating?: string;
    platformType?: string;
  };
  templates: Array<{ id?: string; name: string; status?: string; category?: string; language?: string }>;
  templateSuggestions?: ReviewTemplateSuggestion[];
  checkedAt: string;
};
type ReviewResult = {
  ok: boolean;
  reused?: boolean;
  suggestionKey?: string;
  messageAccepted?: boolean;
  messageReference?: string;
  recipient?: string;
  template?: string | { id?: string; name: string; status?: string; category?: string; language?: string };
  sentAt?: string;
  createdAt?: string;
};
type SignupSession = {
  sessionId: string;
  state?: string;
  nonce?: string;
  publicConfig: { appId: string; graphApiVersion: string; loginOptions: Record<string, any> };
};
type ActiveFlow = SignupSession & {
  empresaId: string;
  code?: string;
  embeddedSignup?: Record<string, any>;
  completing?: boolean;
  timeoutId?: number;
};

function endpoint(template: string, empresaId: string) {
  return template.replace('{empresaId}', encodeURIComponent(empresaId));
}

function reviewRequestId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function suggestionExample(suggestion?: ReviewTemplateSuggestion) {
  if (!suggestion) return '';
  return suggestion.variables.reduce(
    (text, variable) => text.replace(`{{${variable.position}}}`, variable.example),
    suggestion.body,
  );
}

const WhatsAppMetaCard: React.FC<Props> = ({ empresaId }) => {
  const history = useHistory();
  const [status, setStatus] = useState<MetaStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [toast, setToast] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewAssets, setReviewAssets] = useState<ReviewAssets | null>(null);
  const [reviewSection, setReviewSection] = useState<ReviewSection>('messaging');
  const [selectedReviewTemplateKey, setSelectedReviewTemplateKey] = useState('');
  const [reviewRecipient, setReviewRecipient] = useState('');
  const [reviewMessageResult, setReviewMessageResult] = useState<ReviewResult | null>(null);
  const [reviewTemplateResult, setReviewTemplateResult] = useState<ReviewResult | null>(null);
  const mounted = useRef(true);
  const flow = useRef<ActiveFlow | null>(null);

  const clearFlow = useCallback(() => {
    if (flow.current?.timeoutId) window.clearTimeout(flow.current.timeoutId);
    flow.current = null;
  }, []);

  const api = useCallback(<T,>(url: string, method: string, body?: unknown) => new Promise<T>((resolve, reject) => {
    requestService(url, {
      method,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }, history, (value: any) => resolve(value as T), reject, true);
  }), [history]);

  const refresh = useCallback(async () => {
    if (!empresaId) {
      setStatus(null);
      return;
    }
    try {
      const next = await api<MetaStatus>(endpoint(whatsappMetaStatus.url, empresaId), whatsappMetaStatus.method);
      if (mounted.current) setStatus(next);
    } catch (_) {
      if (mounted.current) setStatus(null);
    }
  }, [api, empresaId]);

  const completeIfReady = useCallback(async () => {
    const current = flow.current;
    if (!current || current.completing || !current.code || !current.embeddedSignup) return;
    if (current.empresaId !== empresaId) {
      clearFlow();
      setBusy(false);
      setToast('A empresa selecionada mudou. Inicie o cadastro novamente.');
      return;
    }
    current.completing = true;
    try {
      await api(endpoint(whatsappMetaOnboardingComplete.url, empresaId), whatsappMetaOnboardingComplete.method, {
        sessionId: current.sessionId,
        state: current.state || current.nonce,
        code: current.code,
        embeddedSignup: current.embeddedSignup,
      });
      clearFlow();
      await refresh();
      setConfirmed(false);
      setShowModal(true);
    } catch (error: any) {
      clearFlow();
      setToast(error?.message || 'Não foi possível concluir o Embedded Signup.');
    } finally {
      setBusy(false);
    }
  }, [api, clearFlow, empresaId, refresh]);

  useEffect(() => {
    mounted.current = true;
    clearFlow();
    setBusy(false);
    setShowModal(false);
    setShowReviewModal(false);
    setConfirmed(false);
    setReviewAssets(null);
    setReviewSection('messaging');
    setSelectedReviewTemplateKey('');
    setReviewRecipient('');
    setReviewMessageResult(null);
    setReviewTemplateResult(null);
    refresh();
    return () => { mounted.current = false; clearFlow(); };
  }, [clearFlow, refresh]);

  useEffect(() => {
    const listener = (message: MessageEvent) => {
      if (!isAllowedMetaMessageOrigin(message.origin) || !flow.current) return;
      let payload = message.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch (_) { return; }
      }
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
      const eventName = payload.event || payload.data?.event;
      if (eventName === 'CANCEL' || eventName === 'ERROR') {
        clearFlow();
        setBusy(false);
        setToast(eventName === 'CANCEL' ? 'Cadastro cancelado.' : 'A Meta informou uma falha no cadastro.');
        return;
      }
      if (eventName !== 'FINISH' && eventName !== 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') return;
      const data = payload.data || {};
      flow.current.embeddedSignup = {
        ...data,
        wabaId: data.waba_id || data.wabaId,
        phoneNumberId: data.phone_number_id || data.phoneNumberId,
        businessId: data.business_id || data.businessId,
        onboardingType: 'whatsapp_business_app_onboarding',
        featureType: 'whatsapp_business_app_onboarding',
      };
      void completeIfReady();
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [clearFlow, completeIfReady]);

  const startSignup = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const session = await api<SignupSession>(endpoint(whatsappMetaOnboardingSession.url, empresaId), whatsappMetaOnboardingSession.method, {});
      flow.current = {
        ...session,
        empresaId,
        timeoutId: window.setTimeout(() => {
          clearFlow();
          setBusy(false);
          setToast('O tempo do cadastro expirou. Inicie novamente.');
        }, 5 * 60 * 1000),
      };
      const facebook = await loadMetaFacebookSdk(session.publicConfig.appId, session.publicConfig.graphApiVersion);
      facebook.login((result: any) => {
        if (!flow.current || flow.current.empresaId !== empresaId) return;
        const code = result?.authResponse?.code;
        if (!code) {
          clearFlow();
          setBusy(false);
          setToast('Popup fechado ou autorização cancelada.');
          return;
        }
        flow.current.code = code;
        void completeIfReady();
      }, session.publicConfig.loginOptions);
    } catch (error: any) {
      clearFlow();
      setBusy(false);
      setToast(error?.message || 'Não foi possível iniciar o Embedded Signup.');
    }
  };

  const activate = async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaActivate.url, empresaId), whatsappMetaActivate.method, { confirmouBaileysDesvinculado: true });
      await refresh();
      setShowModal(false);
      setToast('WhatsApp Oficial ativado.');
    } catch (error: any) {
      setToast(error?.message || 'Não foi possível ativar a integração.');
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaTest.url, empresaId), whatsappMetaTest.method, {});
      setToast('WABA, número e token validados com sucesso.');
    } catch (error: any) { setToast(error?.message || 'Falha ao validar a integração.'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    const accepted = window.confirm('Desconectar a integração oficial do Xeguei? Isso não exclui a conta nem remove o número do WhatsApp Business.');
    if (!accepted) return;
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaDisable.url, empresaId), whatsappMetaDisable.method, {});
      await refresh();
      setShowModal(false);
      setToast('Integração oficial desconectada.');
    } catch (error: any) { setToast(error?.message || 'Não foi possível desconectar a integração.'); }
    finally { setBusy(false); }
  };

  const loadReviewAssets = async () => {
    setReviewBusy(true);
    try {
      const result = await api<ReviewAssets>(endpoint(whatsappMetaReviewAssets.url, empresaId), whatsappMetaReviewAssets.method);
      setReviewAssets(result);
      setSelectedReviewTemplateKey((current) => (
        result.templateSuggestions?.some((suggestion) => suggestion.key === current)
          ? current
          : result.templateSuggestions?.[0]?.key || ''
      ));
    } catch (error: any) {
      setToast(error?.message || 'Não foi possível consultar os ativos da Test WABA.');
    } finally { setReviewBusy(false); }
  };

  const openReviewDemo = () => {
    setReviewSection('messaging');
    setReviewMessageResult(null);
    setReviewTemplateResult(null);
    setShowReviewModal(true);
    void loadReviewAssets();
  };

  const sendReviewMessage = async () => {
    if (!reviewRecipient.trim()) {
      setToast('Informe o destinatário autorizado no API Setup.');
      return;
    }
    setReviewBusy(true);
    setReviewMessageResult(null);
    try {
      const result = await api<ReviewResult>(endpoint(whatsappMetaReviewMessage.url, empresaId), whatsappMetaReviewMessage.method, {
        requestId: reviewRequestId('message'),
        recipient: reviewRecipient,
      });
      setReviewMessageResult(result);
      setReviewRecipient('');
      setToast('Mensagem hello_world aceita pela Meta. Confirme o recebimento no celular.');
    } catch (error: any) {
      setToast(error?.message || 'A Meta não concluiu o envio de demonstração.');
    } finally { setReviewBusy(false); }
  };

  const createReviewTemplate = async () => {
    const suggestion = reviewAssets?.templateSuggestions?.find((item) => item.key === selectedReviewTemplateKey);
    if (!suggestion) {
      setToast('Escolha uma sugestão de template do Xeguei.');
      return;
    }
    const accepted = window.confirm(`Preparar o modelo “${suggestion.title}” exclusivamente na Test WABA? Se ele já existir, será reutilizado sem criar uma cópia.`);
    if (!accepted) return;
    setReviewBusy(true);
    setReviewTemplateResult(null);
    try {
      const result = await api<ReviewResult>(endpoint(whatsappMetaReviewTemplate.url, empresaId), whatsappMetaReviewTemplate.method, {
        requestId: reviewRequestId('template'),
        templateKey: suggestion.key,
      });
      setReviewTemplateResult(result);
      await loadReviewAssets();
      setToast(result.reused
        ? 'Template já existente reutilizado. Mostre o status retornado pela Meta.'
        : 'Template criado na Test WABA. Mostre o status retornado pela Meta.');
    } catch (error: any) {
      setToast(error?.message || 'A Meta não concluiu a criação do template.');
    } finally { setReviewBusy(false); }
  };

  if (!status?.available) return null;
  const connected = status.status === 'connected' && status.activeChannel === 'meta_cloud_api';
  const pending = status.status === 'configuration_pending';
  const integration = status.integration;
  const selectedReviewTemplate = reviewAssets?.templateSuggestions?.find(
    (suggestion) => suggestion.key === selectedReviewTemplateKey,
  );

  return <>
    <IonCol size="12" sizeMd="6" sizeLg="6">
      <IonCard style={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <IonCardHeader style={{ position: 'relative' }}>
          {(connected || pending) && <IonBadge color={connected ? 'success' : 'warning'} style={{ position: 'absolute', right: 8, top: 8 }}>
            {connected ? 'Ativo' : 'Configuração pendente'}
          </IonBadge>}
          <IonIcon icon={logoWhatsapp} color="success" style={{ fontSize: 80 }} />
          <IonCardTitle>WhatsApp Oficial — Meta</IonCardTitle>
          <IonCardSubtitle>Meta Cloud API</IonCardSubtitle>
        </IonCardHeader>
        <IonCardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <p>{integration?.verifiedName || 'Conecte o número atual pelo fluxo de Coexistência.'}</p>
          {integration?.displayPhoneNumber && <IonText color="medium"><p>Número: {integration.displayPhoneNumber}</p></IonText>}
          <IonButton size="large" disabled={busy} onClick={pending || connected ? () => setShowModal(true) : startSignup}>
            {busy ? <IonSpinner name="crescent" /> : pending || connected ? 'Configurar' : 'Ativar'}
          </IonButton>
          {status.reviewDemoAvailable && <IonButton fill="outline" color="tertiary" disabled={reviewBusy} onClick={openReviewDemo}>
            Demonstração da App Review
          </IonButton>}
        </IonCardContent>
      </IonCard>
    </IonCol>

    <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
      <IonToolbar color="primary"><IonTitle>WhatsApp Oficial</IonTitle></IonToolbar>
      <IonContent className="ion-padding">
        {(pending || connected) && <IonCard>
          <IonCardHeader><IonCardTitle>Dados da conexão</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <p><strong>Status:</strong> {connected ? 'Ativo' : 'Configuração pendente'}</p>
            <p><strong>WABA ID:</strong> {integration?.wabaId || 'Não informado'}</p>
            <p><strong>Phone Number ID:</strong> {integration?.phoneNumberId || 'Não informado'}</p>
            <p><strong>Número:</strong> {integration?.displayPhoneNumber || 'Não informado'}</p>
            <p><strong>Plataforma:</strong> {integration?.platformType || 'Não informada'}</p>
            <p><strong>WhatsApp Business App:</strong> {integration?.isOnBizApp === true ? 'Coexistência confirmada' : 'Aguardando confirmação'}</p>
            {integration?.lastError?.message && <IonText color="danger"><p>{integration.lastError.message}</p></IonText>}
          </IonCardContent>
        </IonCard>}
        {pending && <>
          <h2>Última etapa</h2>
          <p>Para evitar respostas duplicadas, desconecte o dispositivo antigo utilizado pelo Xeguei em WhatsApp Business → Aparelhos conectados. Não exclua sua conta e não remova o número do WhatsApp Business.</p>
          <IonItem lines="none">
            <IonCheckbox slot="start" checked={confirmed} onIonChange={(event) => setConfirmed(event.detail.checked)} />
            <IonLabel className="ion-text-wrap">Confirmo que desconectei o dispositivo antigo do Xeguei/Baileys.</IonLabel>
          </IonItem>
          <IonButton expand="block" disabled={!confirmed || busy} onClick={activate}>Confirmar e ativar</IonButton>
        </>}
        {connected && <>
          <h2>Integração ativa</h2>
          <p>{integration?.verifiedName} {integration?.displayPhoneNumber}</p>
          <IonButton expand="block" disabled={busy} onClick={testConnection}>Validar conexão</IonButton>
          <IonButton expand="block" color="danger" fill="outline" disabled={busy} onClick={disable}>Desconectar do Xeguei</IonButton>
          <IonText color="medium"><p>Desconectar aqui não exclui a conta nem remove o número do WhatsApp Business.</p></IonText>
        </>}
      </IonContent>
      <IonFooter><IonToolbar><IonButton slot="end" fill="clear" onClick={() => setShowModal(false)}>Fechar</IonButton></IonToolbar></IonFooter>
    </IonModal>

    <IonModal className="xeguei-review-modal" isOpen={showReviewModal} onDidDismiss={() => setShowReviewModal(false)}>
      <IonToolbar className="xeguei-review-toolbar">
        <IonTitle>Demonstração da App Review</IonTitle>
      </IonToolbar>
      <IonContent>
        <div className="xeguei-review-shell">
          <section className="xeguei-review-hero">
            <div>
              <span className="xeguei-review-eyebrow"><IonIcon icon={shieldCheckmarkOutline} /> AMBIENTE CONTROLADO</span>
              <h1>WhatsApp Oficial no Xeguei</h1>
              <p>Demonstre cada permissão separadamente usando apenas os ativos de teste fornecidos pela Meta.</p>
            </div>
            <div className="xeguei-review-hero-badges">
              <span>Graph API {reviewAssets?.graphApiVersion || 'v26.0'}</span>
              <span>Test WABA</span>
            </div>
          </section>

          {reviewBusy && !reviewAssets ? <div className="xeguei-review-loading">
            <IonSpinner name="crescent" />
            <p>Consultando ativos de teste na Meta…</p>
          </div> : reviewAssets ? <>
            <section className="xeguei-review-summary-grid" aria-label="Resumo dos ativos de teste">
              <div className="xeguei-review-summary-card">
                <IonIcon icon={businessOutline} />
                <div><span>Test WABA</span><strong>{reviewAssets.waba.name || 'Test WhatsApp Business Account'}</strong><small>{reviewAssets.waba.id}</small></div>
              </div>
              <div className="xeguei-review-summary-card">
                <IonIcon icon={phonePortraitOutline} />
                <div><span>Número de teste</span><strong>{reviewAssets.phone.displayPhoneNumber || 'Mascarado'}</strong><small>ID {reviewAssets.phone.id}</small></div>
              </div>
              <div className="xeguei-review-summary-card">
                <IonIcon icon={cloudDoneOutline} />
                <div><span>Conexão</span><strong>{reviewAssets.phone.status || 'Não informado'}</strong><small>{reviewAssets.phone.platformType || 'Cloud API'}</small></div>
              </div>
              <div className="xeguei-review-summary-card">
                <IonIcon icon={documentTextOutline} />
                <div><span>Modelos encontrados</span><strong>{reviewAssets.templates.length}</strong><small>Atualizados pela Graph API</small></div>
              </div>
            </section>

            <div className="xeguei-review-refresh-row">
              <span>Última consulta: {new Date(reviewAssets.checkedAt).toLocaleString('pt-BR')}</span>
              <IonButton size="small" fill="clear" disabled={reviewBusy} onClick={loadReviewAssets}>
                <IonIcon slot="start" icon={refreshOutline} /> Atualizar ativos
              </IonButton>
            </div>

            <IonSegment className="xeguei-review-tabs" value={reviewSection} onIonChange={(event) => setReviewSection(String(event.detail.value) as ReviewSection)}>
              <IonSegmentButton value="messaging">
                <IonIcon icon={chatbubbleEllipsesOutline} />
                <IonLabel>Envio de mensagem</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="templates">
                <IonIcon icon={documentTextOutline} />
                <IonLabel>Modelos do Xeguei</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {reviewSection === 'messaging' ? <section className="xeguei-review-section">
              <header className="xeguei-review-section-header">
                <div className="xeguei-review-icon-tile messaging"><IonIcon icon={chatbubbleEllipsesOutline} /></div>
                <div>
                  <span className="xeguei-review-permission">whatsapp_business_messaging</span>
                  <h2>Enviar uma mensagem real de teste</h2>
                  <p>Use o modelo <code>hello_world</code> aprovado pela Meta e confirme o recebimento no celular autorizado.</p>
                </div>
              </header>

              <div className="xeguei-review-two-columns">
                <div className="xeguei-review-action-card">
                  <span className="xeguei-review-step">ETAPA 1</span>
                  <h3>Informe o destinatário autorizado</h3>
                  <p>Digite o número cadastrado no API Setup, com DDI e DDD.</p>
                  <IonItem className="xeguei-review-input" lines="none">
                    <IonIcon slot="start" icon={phonePortraitOutline} />
                    <IonInput
                      type="password"
                      inputMode="tel"
                      value={reviewRecipient}
                      placeholder="Ex.: 5511999999999"
                      onIonChange={(event) => setReviewRecipient(String(event.detail.value || ''))}
                    />
                  </IonItem>
                  <div className="xeguei-review-security-note">
                    <IonIcon icon={shieldCheckmarkOutline} />
                    <span>O número não é salvo e precisa constar na allowlist segura do backend.</span>
                  </div>
                  <IonButton className="xeguei-review-primary-action" expand="block" color="success" disabled={reviewBusy} onClick={sendReviewMessage}>
                    {reviewBusy ? <IonSpinner name="crescent" /> : <><IonIcon slot="start" icon={sendOutline} /> Enviar hello_world</>}
                  </IonButton>
                </div>

                <div className="xeguei-review-preview-card">
                  <span className="xeguei-review-step">PRÉVIA</span>
                  <h3>Mensagem enviada pela Meta</h3>
                  <div className="xeguei-review-phone-preview">
                    <div className="xeguei-review-phone-header"><IonIcon icon={logoWhatsapp} /> WhatsApp</div>
                    <div className="xeguei-review-chat-bubble">
                      <strong>Hello World</strong>
                      <p>Mensagem de teste oficial do WhatsApp Business Platform.</p>
                      <small>agora ✓✓</small>
                    </div>
                  </div>
                </div>
              </div>

              {reviewMessageResult?.messageAccepted && <div className="xeguei-review-result success">
                <IonIcon icon={checkmarkCircleOutline} />
                <div>
                  <h3>Mensagem aceita pela Meta</h3>
                  <p><strong>Modelo:</strong> {typeof reviewMessageResult.template === 'string' ? reviewMessageResult.template : reviewMessageResult.template?.name}</p>
                  <p><strong>Destinatário:</strong> {reviewMessageResult.recipient} · <strong>Referência:</strong> {reviewMessageResult.messageReference}</p>
                  <p>Mostre agora a mensagem recebida no celular durante a gravação.</p>
                </div>
              </div>}
            </section> : <section className="xeguei-review-section">
              <header className="xeguei-review-section-header">
                <div className="xeguei-review-icon-tile templates"><IonIcon icon={documentTextOutline} /></div>
                <div>
                  <span className="xeguei-review-permission">whatsapp_business_management</span>
                  <h2>Modelos sugeridos pelo Xeguei</h2>
                  <p>Escolha um modelo Utility padronizado. O backend aceita somente estas sugestões e reutiliza modelos existentes.</p>
                </div>
              </header>

              <div className="xeguei-review-template-layout">
                <div>
                  <span className="xeguei-review-step">1 · ESCOLHA UMA SUGESTÃO</span>
                  <div className="xeguei-review-suggestion-grid">
                    {!reviewAssets.templateSuggestions?.length && <p className="xeguei-review-empty-catalog">O catálogo de modelos ainda não foi carregado. Atualize os ativos após publicar o backend.</p>}
                    {(reviewAssets.templateSuggestions || []).map((suggestion) => {
                      const selected = suggestion.key === selectedReviewTemplateKey;
                      return <button
                        type="button"
                        key={suggestion.key}
                        aria-pressed={selected}
                        className={`xeguei-review-suggestion${selected ? ' selected' : ''}`}
                        onClick={() => setSelectedReviewTemplateKey(suggestion.key)}
                      >
                        <div className="xeguei-review-suggestion-heading">
                          <span>{suggestion.title}</span>
                          {selected && <IonIcon icon={checkmarkCircleOutline} />}
                        </div>
                        <p>{suggestion.description}</p>
                        <code>{suggestion.name}</code>
                      </button>;
                    })}
                  </div>
                </div>

                <aside className="xeguei-review-template-preview">
                  <span className="xeguei-review-step">2 · CONFIRA A PRÉVIA</span>
                  {selectedReviewTemplate ? <>
                    <div className="xeguei-review-template-meta">
                      <IonBadge color="primary">{selectedReviewTemplate.category}</IonBadge>
                      <span>{selectedReviewTemplate.language}</span>
                    </div>
                    <h3>{selectedReviewTemplate.title}</h3>
                    <code className="xeguei-review-template-name">{selectedReviewTemplate.name}</code>
                    <div className="xeguei-review-chat-bubble template">{suggestionExample(selectedReviewTemplate)}</div>
                    <div className="xeguei-review-variable-list">
                      {selectedReviewTemplate.variables.map((variable) => <span key={variable.position}>
                        {`{{${variable.position}}}`} {variable.label}: <strong>{variable.example}</strong>
                      </span>)}
                    </div>
                    <IonButton className="xeguei-review-primary-action" expand="block" color="tertiary" disabled={reviewBusy} onClick={createReviewTemplate}>
                      {reviewBusy ? <IonSpinner name="crescent" /> : <><IonIcon slot="start" icon={documentTextOutline} /> Criar ou reutilizar modelo</>}
                    </IonButton>
                    <p className="xeguei-review-safe-copy"><IonIcon icon={shieldCheckmarkOutline} /> Somente a Test WABA será alterada. Nenhum número será criado ou migrado.</p>
                  </> : <p>Selecione uma sugestão para visualizar o modelo.</p>}
                </aside>
              </div>

              {reviewTemplateResult && typeof reviewTemplateResult.template === 'object' && <div className="xeguei-review-result success">
                <IonIcon icon={checkmarkCircleOutline} />
                <div>
                  <h3>{reviewTemplateResult.reused ? 'Modelo existente reutilizado' : 'Modelo enviado para a Meta'}</h3>
                  <p><strong>Nome:</strong> {reviewTemplateResult.template.name}</p>
                  <p><strong>ID:</strong> {reviewTemplateResult.template.id || 'Aguardando retorno'} · <strong>Status:</strong> {reviewTemplateResult.template.status || 'PENDING'} · <strong>Categoria:</strong> {reviewTemplateResult.template.category}</p>
                </div>
              </div>}

              <details className="xeguei-review-existing-templates">
                <summary>Modelos já encontrados na Test WABA ({reviewAssets.templates.length})</summary>
                <div className="xeguei-review-template-list">
                  {reviewAssets.templates.length ? reviewAssets.templates.slice(0, 20).map((template) => <div key={template.id || template.name}>
                    <code>{template.name}</code>
                    <span>{template.status || 'SEM STATUS'}</span>
                    <small>{template.category || 'sem categoria'} · {template.language || 'sem idioma'}</small>
                  </div>) : <p>Nenhum modelo encontrado.</p>}
                </div>
              </details>
            </section>}
          </> : <div className="xeguei-review-loading">
            <p>Não foi possível carregar os ativos de teste.</p>
            <IonButton fill="outline" onClick={loadReviewAssets}>Tentar novamente</IonButton>
          </div>}
        </div>
      </IonContent>
      <IonFooter className="xeguei-review-footer"><IonToolbar><IonButton slot="end" fill="clear" onClick={() => setShowReviewModal(false)}>Fechar demonstração</IonButton></IonToolbar></IonFooter>
    </IonModal>
    <IonToast isOpen={Boolean(toast)} message={toast} duration={5000} onDidDismiss={() => setToast('')} />
  </>;
};

export default WhatsAppMetaCard;
